import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  UtensilsCrossed as Restaurant,
  Store,
  MapPin,
  Phone,
  Globe,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Upload,
  AlertCircle,
  FileText,
  CreditCard,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  BRAND,
  EDITORIAL_FONT,
  PatternTile,
  Eyebrow,
  EditorialShell,
  EditorialCard,
  SerifHeading,
  PillButton,
  useEditorialPalette,
} from "../components/editorial/EditorialPrimitives";

const API_URL = import.meta.env.VITE_API_URL;
const HALAL_API_URL = import.meta.env.VITE_HALAL_API;
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
// The Stripe bypass is a development-only affordance, so production ignores the
// flag outright rather than trusting the build environment not to set it. This
// is deliberately `&&` on import.meta.env.PROD, which Vite replaces with a
// literal at build time, so a production bundle folds this to false and drops
// the bypass branches entirely.
//
// This used to `throw` at module scope when the flag was set in a production
// build. That turned a misconfigured environment variable into a blank page for
// the entire site, because the throw runs when the module is first evaluated,
// long before any router or error boundary can contain it. Refusing to honour
// the flag achieves the same safety goal, since the bypass cannot run either
// way, without taking every other page down with it.
const DEBUG_SKIP_STRIPE =
  !import.meta.env.PROD && import.meta.env.VITE_DEBUG_SKIP_STRIPE === "true";
const DEBUG_PASSWORD = import.meta.env.VITE_DEBUG_PASSWORD;

if (import.meta.env.PROD && import.meta.env.VITE_DEBUG_SKIP_STRIPE === "true") {
  // Loud, but survivable. Someone needs to unset this in the build environment.
  console.error(
    "VITE_DEBUG_SKIP_STRIPE is set in a production build and is being ignored. " +
      "Unset it in the deployment environment."
  );
}

const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : Promise.resolve(null);

const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, "");
  const limitedNumber = phoneNumber.slice(0, 11);
  if (limitedNumber.length === 0) return "";
  if (limitedNumber.length <= 1) return `+${limitedNumber}`;
  if (limitedNumber.length <= 4) return `+${limitedNumber.slice(0, 1)} (${limitedNumber.slice(1)}`;
  if (limitedNumber.length <= 7)
    return `+${limitedNumber.slice(0, 1)} (${limitedNumber.slice(1, 4)}) ${limitedNumber.slice(4)}`;
  return `+${limitedNumber.slice(0, 1)} (${limitedNumber.slice(1, 4)}) ${limitedNumber.slice(4, 7)}-${limitedNumber.slice(7)}`;
};

const cleanPhoneNumber = (value) => value.replace(/\D/g, "");

const inputClass =
  "h-12 rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]";
const makeInputStyle = (palette) => ({
  background: "#ffffff",
  borderColor: palette.hairline,
  color: palette.ink,
  fontFamily: EDITORIAL_FONT.sans,
});
const makeLabelStyle = (palette) => ({
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: palette.muted,
  fontFamily: EDITORIAL_FONT.sans,
});

const ErrorAlert = ({ children }) => (
  <div
    className="rounded-xl p-4"
    style={{
      background: BRAND.terracotta + "15",
      border: `1px solid ${BRAND.terracotta}40`,
      color: BRAND.terracotta,
    }}
  >
    <div className="flex items-start gap-3">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <p className="text-sm font-medium">{children}</p>
    </div>
  </div>
);

const SuccessPanel = ({ title, subtitle, loadingLabel }) => {
  const palette = useEditorialPalette();
  return (
  <div className="flex flex-col items-center justify-center py-10 sm:py-14">
    <div className="relative mb-6">
      <div
        className="absolute -inset-2 animate-pulse rounded-3xl"
        style={{ border: `1px solid ${BRAND.sage}40` }}
      />
    </div>
    <SerifHeading level={2} className="mb-2" style={{ fontSize: 26, lineHeight: 1 }}>
      {title}
    </SerifHeading>
    <p className="mb-5 max-w-xs text-center text-sm leading-relaxed" style={{ color: palette.muted }}>
      {subtitle}
    </p>
    <div
      className="inline-flex items-center gap-2.5 rounded-full px-4 py-2"
      style={{ background: BRAND.sage + "15", border: `1px solid ${BRAND.sage}40` }}
    >
      <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: BRAND.sage }} />
      <span className="text-xs font-semibold" style={{ color: BRAND.sage, letterSpacing: "0.04em" }}>
        {loadingLabel}
      </span>
    </div>
  </div>
  );
};

const UploadProgress = ({ progress }) => {
  const palette = useEditorialPalette();
  return (
  <div className="flex flex-col items-center justify-center py-10 sm:py-14">
    <div className="relative mb-6">
      <div
        className="flex h-16 w-16 items-center justify-center rounded-2xl"
        style={{ background: BRAND.gold + "15" }}
      >
        <Upload className="h-6 w-6" style={{ color: BRAND.gold }} />
      </div>
      <svg className="absolute -inset-2" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="36" fill="none" stroke={BRAND.gold + "20"} strokeWidth="2" />
        <circle
          cx="40"
          cy="40"
          r="36"
          fill="none"
          stroke={BRAND.gold}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={`${2 * Math.PI * 36}`}
          strokeDashoffset={`${2 * Math.PI * 36 * (1 - progress / 100)}`}
          transform="rotate(-90 40 40)"
          className="transition-all duration-500 ease-out"
        />
      </svg>
    </div>
    <p className="mb-1 text-base font-semibold" style={{ color: palette.ink }}>
      {progress < 30
        ? "Preparing upload…"
        : progress < 70
        ? "Uploading certificate…"
        : "Finalizing…"}
    </p>
    <p className="text-xs" style={{ color: palette.muted }}>
      {progress}% complete
    </p>
  </div>
  );
};

/* ───────── Express checkout ───────── */
const ExpressCheckout = ({ onSuccess, onError, userEmail, postalCode, country = "US" }) => {
  const palette = useEditorialPalette();
  const stripe = useStripe();
  const elements = useElements();
  const [visible, setVisible] = useState(true);

  const onConfirm = async () => {
    if (!stripe || !elements) return;
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              email: userEmail,
              address: { country, postal_code: postalCode || undefined },
            },
          },
        },
      });
      if (error) {
        console.error("Express checkout setup error:", error.message);
        onError("An error has occurred. Please try again.");
      } else {
        onSuccess();
      }
    } catch (err) {
      console.error("Express checkout error:", err);
      onError("An error has occurred. Please try again.");
    }
  };

  if (!visible) return null;

  return (
    <div className="space-y-4">
      <ExpressCheckoutElement
        onConfirm={onConfirm}
        onReady={({ availablePaymentMethods }) => {
          if (!availablePaymentMethods || Object.keys(availablePaymentMethods).length === 0) {
            setVisible(false);
          }
        }}
        options={{
          buttonType: { applePay: "subscribe", googlePay: "subscribe" },
          layout: { maxColumns: 2, maxRows: 1 },
        }}
      />
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t" style={{ borderColor: palette.hairline }} />
        </div>
        <div className="relative flex justify-center text-xs">
          <span
            className="px-3 font-medium"
            style={{ background: palette.paper, color: palette.muted }}
          >
            Or pay with card
          </span>
        </div>
      </div>
    </div>
  );
};

/* ───────── Stripe payment form ───────── */
const StripePaymentForm = ({ onSuccess, onError, userEmail, postalCode, country = "US" }) => {
  const palette = useEditorialPalette();
  const inputStyle = makeInputStyle(palette);
  const labelStyle = makeLabelStyle(palette);
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              email: userEmail,
              address: { country, postal_code: postalCode || undefined },
            },
          },
        },
      });

      if (error) {
        console.error("Stripe setup error:", error.message);
        onError("An error has occurred. Please try again.");
        setIsProcessing(false);
      } else {
        onSuccess();
      }
    } catch (err) {
      console.error("Payment setup error:", err);
      onError("An error has occurred. Please try again.");
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <ExpressCheckout
        onSuccess={onSuccess}
        onError={onError}
        userEmail={userEmail}
        postalCode={postalCode}
        country={country}
      />

      <div className="space-y-2">
        <Label style={labelStyle}>Email</Label>
        <Input
          value={userEmail}
          disabled
          className={inputClass}
          style={{ ...inputStyle, background: palette.paper, color: palette.muted }}
        />
      </div>

      <div className="space-y-2">
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                email: "never",
                address: { country: "never", postalCode: "never" },
              },
            },
          }}
        />
      </div>

      <PillButton
        type="submit"
        variant="primary"
        disabled={!stripe || isProcessing}
        className="w-full justify-center"
        size="lg"
      >
        {isProcessing ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>
            Complete setup
            <span aria-hidden>→</span>
          </>
        )}
      </PillButton>

      <p className="text-center text-xs" style={{ color: palette.muted }}>
        Powered by <span className="font-semibold" style={{ color: "#635bff" }}>Stripe</span>
      </p>
    </form>
  );
};

/* ───────── Form input wrapper ───────── */
const FormInput = ({ icon: Icon, label, error, ...props }) => {
  const palette = useEditorialPalette();
  const inputStyle = makeInputStyle(palette);
  const labelStyle = makeLabelStyle(palette);
  return (
  <div className="space-y-2">
    {label && <Label style={labelStyle}>{label}</Label>}
    <div className="relative">
      {Icon && (
        <div
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          style={{ color: palette.muted }}
        >
          <Icon size={18} />
        </div>
      )}
      <Input
        className={`${inputClass} ${Icon ? "pl-10" : ""} ${error ? "border-[#b85c3d]" : ""}`}
        style={inputStyle}
        {...props}
      />
    </div>
    {error && (
      <p className="flex items-center gap-1 text-sm" style={{ color: BRAND.terracotta }}>
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
  );
};

export default function HalalSignup() {
  const palette = useEditorialPalette();
  const inputStyle = makeInputStyle(palette);
  const labelStyle = makeLabelStyle(palette);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [sourcingExplanation, setSourcingExplanation] = useState("");
  const [selectedTier, setSelectedTier] = useState("pro");
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [applicationId, setApplicationId] = useState(null);

  const [debugPassword, setDebugPassword] = useState("");
  const [debugPasswordError, setDebugPasswordError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const fromMobile = location.state?.fromMobile === true;
  const { user } = useAuth();

  const [restaurantData, setRestaurantData] = useState({
    businessType: "restaurant",
    restaurantName: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    website: "",
  });

  const [agreedToSubscriptionTerms, setAgreedToSubscriptionTerms] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressInputValue, setAddressInputValue] = useState("");
  const [loadingPlaces, setLoadingPlaces] = useState(false);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!applicationId) {
      const crypto = window.crypto || window.msCrypto;
      const array = new Uint8Array(20);
      crypto.getRandomValues(array);
      const id = Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
      setApplicationId(id);
    }
  }, [applicationId]);

  const fetchAddressSuggestions = async (input) => {
    if (!input || input.trim().length < 2) {
      setAddressSuggestions([]);
      setLoadingPlaces(false);
      return;
    }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setLoadingPlaces(true);
    const query = input.trim();

    try {
      const response = await fetch(
        `${API_URL}/v1/places/autocomplete?input=${encodeURIComponent(query)}`,
        { signal: abortControllerRef.current.signal }
      );
      if (!response.ok) throw new Error(`Places API error: ${response.status}`);
      const data = await response.json();
      if (data.ok && Array.isArray(data.suggestions)) {
        setAddressSuggestions(data.suggestions);
      } else {
        setAddressSuggestions([]);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("[Places] Error fetching suggestions:", err);
        setAddressSuggestions([]);
      }
    } finally {
      setLoadingPlaces(false);
    }
  };

  const handleAddressSelect = async (event, value) => {
    if (!value) return;

    const applyComponents = (components) => {
      let streetNumber = "";
      let route = "";
      let city = "";
      let state = "";
      let zipCode = "";

      components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) streetNumber = component.long_name || component.longText || "";
        if (types.includes("route")) route = component.long_name || component.longText || "";
        if (types.includes("locality")) city = component.long_name || component.longText || "";
        if (types.includes("administrative_area_level_1")) state = component.short_name || component.shortText || "";
        if (types.includes("postal_code")) zipCode = component.long_name || component.longText || "";
      });

      const fullAddress = `${streetNumber} ${route}`.trim();
      setRestaurantData({ ...restaurantData, address: fullAddress, city, state, zipCode });
      setAddressInputValue(fullAddress);
    };

    try {
      if (value.addressComponents) {
        applyComponents(value.addressComponents);
        return;
      }
      if (value.placeId) {
        const response = await fetch(
          `${API_URL}/v1/places/details?placeId=${encodeURIComponent(value.placeId)}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.ok && Array.isArray(data.addressComponents)) {
            applyComponents(data.addressComponents);
          }
        }
      }
    } catch (err) {
      console.error("[Places] Error parsing address:", err);
    }
  };

  const handleChange = (e) => {
    setRestaurantData({ ...restaurantData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError("File size must be less than 10MB");
      return;
    }
    const validTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      setError("Please upload a PDF or image file (JPEG, PNG)");
      return;
    }

    setCertificateFile(file);
    setError("");

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => setCertificatePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setCertificatePreview(null);
    }
  };

  const handleContinueWithPlan = async () => {
    if (!user || !applicationId) {
      setError("Please log in to continue");
      return;
    }
    if (!restaurantData.restaurantName || !restaurantData.city || !restaurantData.phone) {
      setError("Please complete the restaurant details first");
      return;
    }
    if (!certificateFile && !sourcingExplanation.trim()) {
      setError("Please upload your halal certificate or provide a sourcing explanation");
      return;
    }
    if (!agreedToSubscriptionTerms) {
      setError("You must agree to the Terms of Service and Subscription Terms to continue");
      return;
    }

    if (DEBUG_SKIP_STRIPE) {
      setCurrentStep(3);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const idToken = await user.getIdToken(true);
      const response = await fetch(`${HALAL_API_URL}/v1/halal/create-setup-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ tier: selectedTier, applicationId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to create checkout session");
      }

      const data = await response.json();
      if (data.ok && data.clientSecret) {
        setStripeClientSecret(data.clientSecret);
        setCurrentStep(3);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError(err.message || "Failed to proceed to payment");
    } finally {
      setLoading(false);
    }
  };

  const handleNextToPlans = (e) => {
    e.preventDefault();
    setError("");

    if (!restaurantData.restaurantName || !restaurantData.city || !restaurantData.phone) {
      setError("Please fill in all required fields");
      return;
    }
    if (!certificateFile && !sourcingExplanation.trim()) {
      setError("Please upload your halal certificate or provide a sourcing explanation");
      return;
    }

    setCurrentStep(2);
  };

  const handleStripeComplete = async () => {
    setLoading(true);
    setUploadProgress(10);
    setError("");

    try {
      const idToken = await user.getIdToken(true);
      const cleanedRestaurantData = {
        ...restaurantData,
        phone: cleanPhoneNumber(restaurantData.phone),
      };

      const uploadBody = {
        restaurantData: cleanedRestaurantData,
        applicationId,
        ...(certificateFile
          ? { fileContentType: certificateFile.type }
          : { sourcingExplanation: sourcingExplanation.trim() }),
      };

      const response = await fetch(`${HALAL_API_URL}/v1/halal/upload-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(uploadBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409) {
          setError("You already have a pending application. Please wait for it to be reviewed or contact support.");
        } else if (response.status === 403) {
          setError("Please verify your email address before submitting an application.");
        } else {
          console.error("Upload URL error:", errorData);
          setError("An error has occurred. Please try again.");
        }
        setLoading(false);
        setUploadProgress(0);
        return;
      }

      const { uploadUrl, halalId } = await response.json();
      setUploadProgress(30);

      if (certificateFile && uploadUrl) {
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": certificateFile.type },
          body: certificateFile,
        });
        if (!uploadResponse.ok) throw new Error("Failed to upload certificate");
      }

      setUploadProgress(70);

      const completeResponse = await fetch(
        `${HALAL_API_URL}/v1/halal/application/${halalId}/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        }
      );

      if (!completeResponse.ok) throw new Error("BACKEND_ERROR");

      const completeData = await completeResponse.json();
      if (completeData.ok && completeData.user) {
        localStorage.setItem(
          "muslim_server_user",
          JSON.stringify({ ...completeData.user, email: user.email })
        );
      }

      setUploadProgress(100);
      setSuccess(true);

      setTimeout(() => {
        if (fromMobile) {
          window.location.href = "muslim://signup-complete";
        } else {
          window.location.href = "/halal";
        }
      }, 2000);
    } catch (err) {
      console.error("Error submitting restaurant info:", err);
      setError("An error has occurred. Please try again.");
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDebugPasswordSubmit = async () => {
    setDebugPasswordError("");
    if (!DEBUG_PASSWORD || debugPassword !== DEBUG_PASSWORD) {
      setDebugPasswordError("Incorrect password");
      return;
    }

    setLoading(true);
    setError("");
    setUploadProgress(10);

    try {
      const idToken = await user.getIdToken(true);
      const cleanedRestaurantData = {
        ...restaurantData,
        phone: cleanPhoneNumber(restaurantData.phone),
      };

      const debugUploadBody = {
        restaurantData: cleanedRestaurantData,
        applicationId,
        ...(certificateFile
          ? { fileContentType: certificateFile.type }
          : { sourcingExplanation: sourcingExplanation.trim() }),
      };

      const response = await fetch(`${HALAL_API_URL}/v1/halal/upload-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(debugUploadBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409) {
          setError("You already have a pending application. Please wait for it to be reviewed.");
        } else {
          console.error("Upload URL error:", errorData);
          setError("An error has occurred. Please try again.");
        }
        setLoading(false);
        setUploadProgress(0);
        return;
      }

      const { uploadUrl, halalId } = await response.json();
      setUploadProgress(30);

      if (certificateFile && uploadUrl) {
        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": certificateFile.type },
          body: certificateFile,
        });
        if (!uploadResponse.ok) throw new Error("Failed to upload certificate");
      }

      setUploadProgress(70);

      const completeResponse = await fetch(
        `${HALAL_API_URL}/v1/halal/application/${halalId}/complete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        }
      );

      if (!completeResponse.ok) throw new Error("BACKEND_ERROR");

      const completeData = await completeResponse.json();
      if (completeData.ok && completeData.user) {
        localStorage.setItem(
          "muslim_server_user",
          JSON.stringify({ ...completeData.user, email: user.email })
        );
      }

      setUploadProgress(100);
      setSuccess(true);

      setTimeout(() => {
        if (fromMobile) {
          window.location.href = "muslim://signup-complete";
        } else {
          window.location.href = "/halal";
        }
      }, 2000);
    } catch (err) {
      console.error("Error submitting application (debug mode):", err);
      setError("An error has occurred. Please try again.");
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const stepLabels = ["Details", "Plan", "Payment"];

  const renderStripeCheckout = () => {
    if (DEBUG_SKIP_STRIPE) {
      return (
        <div className="space-y-6">
          <button
            onClick={() => setCurrentStep(2)}
            disabled={loading || success}
            className="-ml-2 inline-flex items-center gap-2 text-sm transition-colors hover:opacity-70 disabled:opacity-50"
            style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to plans
          </button>

          <div className="space-y-3 text-center">
            <div className="mx-auto mb-1 flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: BRAND.gold + "18" }}>
              <AlertCircle className="h-6 w-6" style={{ color: BRAND.gold }} />
            </div>
            <SerifHeading level={2} style={{ fontSize: 26, lineHeight: 1 }}>
              Debug <em style={{ color: BRAND.gold, fontWeight: 400 }}>mode.</em>
            </SerifHeading>
            <p className="mx-auto max-w-sm text-sm leading-relaxed" style={{ color: palette.muted }}>
              Stripe payment is bypassed. Enter password to continue.
            </p>
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold"
              style={{ background: BRAND.gold + "20", color: BRAND.goldDeep, border: `1px solid ${BRAND.gold}40` }}
            >
              Debug mode active
            </div>
          </div>

          {error && <ErrorAlert>{error}</ErrorAlert>}
          {debugPasswordError && <ErrorAlert>{debugPasswordError}</ErrorAlert>}

          {success && (
            <SuccessPanel
              title="Application submitted."
              subtitle="Your application is being reviewed. You'll hear from us soon."
              loadingLabel="Redirecting…"
            />
          )}

          {uploadProgress > 0 && uploadProgress < 100 && <UploadProgress progress={uploadProgress} />}

          {!loading && !success && (
            <EditorialCard variant="ivory" className="space-y-4 p-5 sm:p-6">
              <div className="space-y-2">
                <Label style={labelStyle}>Debug password</Label>
                <Input
                  type="password"
                  value={debugPassword}
                  onChange={(e) => setDebugPassword(e.target.value)}
                  placeholder="Enter debug password"
                  className={inputClass}
                  style={inputStyle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleDebugPasswordSubmit();
                  }}
                />
              </div>

              <PillButton
                variant="primary"
                onClick={handleDebugPasswordSubmit}
                disabled={!debugPassword || loading}
                className="w-full justify-center"
                size="lg"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Submit application
                    <span aria-hidden>→</span>
                  </>
                )}
              </PillButton>

              <p className="text-center text-xs font-medium" style={{ color: BRAND.gold }}>
                Debug mode: Stripe payment bypassed
              </p>
            </EditorialCard>
          )}
        </div>
      );
    }

    const stripeElementsOptions = {
      clientSecret: stripeClientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: BRAND.gold,
          colorBackground: "#ffffff",
          colorText: palette.ink,
          colorDanger: BRAND.terracotta,
          fontFamily: "'Work Sans', system-ui, sans-serif",
          spacingUnit: "4px",
          borderRadius: "12px",
          fontSizeBase: "14px",
        },
        rules: {
          ".Input": {
            padding: "12px",
            border: `1px solid ${palette.hairline}`,
            boxShadow: "none",
          },
          ".Input:focus": {
            borderColor: BRAND.gold,
            boxShadow: `0 0 0 3px ${BRAND.gold}26`,
          },
          ".Label": {
            fontSize: "11px",
            fontWeight: "600",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.14em",
            color: palette.muted,
          },
        },
      },
    };

    return (
      <div className="space-y-6">
        <button
          onClick={() => setCurrentStep(2)}
          disabled={loading || success}
          className="-ml-2 inline-flex items-center gap-2 text-sm transition-colors hover:opacity-70 disabled:opacity-50"
          style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to plans
        </button>

        <div className="space-y-3 text-center">
          <div className="mx-auto mb-1 flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: BRAND.gold + "15" }}>
            <CreditCard className="h-6 w-6" style={{ color: BRAND.gold }} />
          </div>
          <SerifHeading level={2} style={{ fontSize: 28, lineHeight: 1 }}>
            Payment <em style={{ color: BRAND.gold, fontWeight: 400 }}>setup.</em>
          </SerifHeading>
          <p className="mx-auto max-w-sm text-sm leading-relaxed" style={{ color: palette.muted }}>
            Save your payment method securely. You'll only be charged after your application is
            approved.
          </p>
        </div>

        {error && <ErrorAlert>{error}</ErrorAlert>}

        {success && (
          <SuccessPanel
            title="Application submitted."
            subtitle="Your application is being reviewed. You'll hear from us soon."
            loadingLabel="Redirecting to your dashboard…"
          />
        )}

        {uploadProgress > 0 && uploadProgress < 100 && <UploadProgress progress={uploadProgress} />}

        {!loading && !success && stripeClientSecret && (
          <EditorialCard variant="ivory" className="p-5 sm:p-6">
            <Elements stripe={stripePromise} options={stripeElementsOptions}>
              <StripePaymentForm
                onSuccess={handleStripeComplete}
                onError={(errorMsg) => setError(errorMsg)}
                userEmail={user?.email || ""}
                postalCode={restaurantData?.zipCode || ""}
                country="US"
              />
            </Elements>
          </EditorialCard>
        )}

        {!stripeClientSecret && !loading && !success && (
          <div className="flex flex-col items-center justify-center gap-4 py-14">
            <div className="relative">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: BRAND.gold + "15" }}
              >
                <Loader2 className="h-6 w-6 animate-spin" style={{ color: BRAND.gold }} />
              </div>
            </div>
            <p className="text-sm font-medium" style={{ color: palette.muted }}>
              Loading payment form…
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderPlanSelection = () => {
    const plans = [
      {
        id: "basic",
        name: "Basic",
        price: 25,
        description: "Perfect for getting started",
        features: [
          "Business directory listing",
          "Full contact details",
          "Map location",
          "Halal certification badge",
        ],
      },
      {
        id: "pro",
        name: "Pro",
        price: 75,
        description: "Best for growing businesses",
        popular: true,
        features: [
          "Everything in Basic",
          "Video posts on feed",
          "Showcase your menu",
          "Featured placement",
          "Priority support",
        ],
      },
    ];

    return (
      <div className="space-y-6">
        <button
          onClick={() => setCurrentStep(1)}
          disabled={loading}
          className="-ml-2 inline-flex items-center gap-2 text-sm transition-colors hover:opacity-70 disabled:opacity-50"
          style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to details
        </button>

        <div className="space-y-2 text-center">
          <SerifHeading level={2} style={{ fontSize: 30, lineHeight: 1 }}>
            Choose your <em style={{ color: BRAND.gold, fontWeight: 400 }}>plan.</em>
          </SerifHeading>
          <p className="text-sm" style={{ color: palette.muted }}>
            Select the plan that fits your business needs.
          </p>
        </div>

        {error && <ErrorAlert>{error}</ErrorAlert>}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {plans.map((plan) => {
            const selected = selectedTier === plan.id;
            return (
              <button
                key={plan.id}
                type="button"
                className={`relative cursor-pointer rounded-2xl p-6 text-left transition-all ${
                  loading ? "pointer-events-none opacity-70" : ""
                }`}
                style={{
                  background: selected ? palette.paper : "#ffffff",
                  border: `1px solid ${selected ? BRAND.gold : palette.hairline}`,
                  boxShadow: selected ? `0 4px 24px -6px rgba(191,154,47,0.25)` : "none",
                }}
                onClick={() => !loading && setSelectedTier(plan.id)}
              >
                {plan.popular && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-semibold"
                    style={{
                      background: BRAND.gold,
                      color: "#fff",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    Most popular
                  </div>
                )}
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <SerifHeading level={3} style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.1 }}>
                      {plan.name}
                    </SerifHeading>
                    <p className="text-sm" style={{ color: palette.muted }}>
                      {plan.description}
                    </p>
                  </div>
                  <div
                    className="flex h-5 w-5 items-center justify-center rounded-full transition-colors"
                    style={{
                      background: selected ? BRAND.gold : "transparent",
                      border: selected ? "none" : `2px solid ${palette.hairline}`,
                    }}
                  >
                    {selected && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
                        <path d="M1 5l2.5 2.5L9 2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>

                <div className="mb-5 flex items-baseline gap-1">
                  <span
                    style={{
                      fontFamily: EDITORIAL_FONT.serif,
                      fontSize: 40,
                      fontWeight: 500,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    ${plan.price}
                  </span>
                  <span className="text-sm" style={{ color: palette.muted }}>
                    /month
                  </span>
                </div>

                <ul className="space-y-2.5">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm" style={{ color: palette.ink }}>
                      <span style={{ color: BRAND.gold, fontSize: 10 }}>◆</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <div
          className="rounded-xl p-4"
          style={{ background: palette.paper, border: `1px solid ${palette.hairline}` }}
        >
          <div className="flex items-start gap-3">
            <Checkbox
              id="subscription-terms"
              checked={agreedToSubscriptionTerms}
              onCheckedChange={(checked) => setAgreedToSubscriptionTerms(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="subscription-terms"
              className="cursor-pointer text-sm leading-relaxed"
              style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
            >
              I agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.gold, fontWeight: 600, textDecoration: "underline" }} onClick={(e) => e.stopPropagation()}>
                Terms of Service
              </a>
              ,{" "}
              <a href="/terms?tab=subscription" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.gold, fontWeight: 600, textDecoration: "underline" }} onClick={(e) => e.stopPropagation()}>
                Subscription Terms
              </a>
              , and{" "}
              <a href="/policy" target="_blank" rel="noopener noreferrer" style={{ color: BRAND.gold, fontWeight: 600, textDecoration: "underline" }} onClick={(e) => e.stopPropagation()}>
                Privacy Policy
              </a>
              . I understand that my subscription will be billed monthly and I authorize recurring
              charges to my payment method after my application is approved.
            </Label>
          </div>
        </div>

        <PillButton
          variant="primary"
          disabled={loading || !agreedToSubscriptionTerms}
          onClick={handleContinueWithPlan}
          className="w-full justify-center"
          size="lg"
        >
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Continue with {selectedTier === "basic" ? "Basic" : "Pro"} plan
              <span aria-hidden>→</span>
            </>
          )}
        </PillButton>
      </div>
    );
  };

  const renderRestaurantForm = () => (
    <div className="space-y-6">
      <div className="space-y-3 text-center">
        <div className="mx-auto mb-1 flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: BRAND.gold + "15" }}>
          {restaurantData.businessType === "store" ? (
            <Store className="h-6 w-6" style={{ color: BRAND.gold }} />
          ) : (
            <Restaurant className="h-6 w-6" style={{ color: BRAND.gold }} />
          )}
        </div>
        <SerifHeading level={2} style={{ fontSize: 28, lineHeight: 1 }}>
          Business <em style={{ color: BRAND.gold, fontWeight: 400 }}>details.</em>
        </SerifHeading>
        <p className="text-sm leading-relaxed" style={{ color: palette.muted }}>
          Tell us about your halal business.
        </p>
      </div>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      {success && (
        <SuccessPanel
          title="Details saved."
          subtitle="Your business information has been saved. Moving to plan selection…"
          loadingLabel="Loading plans…"
        />
      )}

      <form onSubmit={handleNextToPlans} className="space-y-5">
        <div className="space-y-2">
          <Label style={labelStyle}>Business type</Label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: "restaurant", icon: Restaurant, label: "Restaurant" },
              { key: "store", icon: Store, label: "Store" },
            ].map(({ key, icon: Icon, label }) => {
              const selected = restaurantData.businessType === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRestaurantData((prev) => ({ ...prev, businessType: key }))}
                  className="flex flex-col items-center gap-2 rounded-xl p-4 transition-all"
                  style={{
                    background: selected ? palette.paper : "#ffffff",
                    border: `2px solid ${selected ? BRAND.gold : palette.hairline}`,
                    color: selected ? BRAND.gold : palette.muted,
                  }}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-sm font-medium">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <FormInput
          label={restaurantData.businessType === "store" ? "Store name *" : "Restaurant name *"}
          name="restaurantName"
          value={restaurantData.restaurantName}
          onChange={handleChange}
          required
          autoFocus
          placeholder={
            restaurantData.businessType === "store"
              ? "Enter your store name"
              : "Enter your restaurant name"
          }
        />

        <div className="relative space-y-2">
          <Label style={labelStyle}>Street address</Label>
          <div className="relative">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2" style={{ color: palette.muted }}>
              <MapPin size={18} />
            </div>
            <Input
              className={`${inputClass} pl-10`}
              style={inputStyle}
              placeholder="123 Main St"
              value={addressInputValue}
              onChange={(e) => {
                setAddressInputValue(e.target.value);
                if (debounceRef.current) clearTimeout(debounceRef.current);
                debounceRef.current = setTimeout(() => {
                  fetchAddressSuggestions(e.target.value);
                }, 250);
              }}
              autoComplete="off"
            />
            {loadingPlaces && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: palette.muted }} />
              </div>
            )}
          </div>
          {addressSuggestions.length > 0 && (
            <div
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl shadow-lg"
              style={{ background: "#ffffff", border: `1px solid ${palette.hairline}` }}
            >
              {addressSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full px-4 py-3 text-left text-sm transition-colors"
                  style={{
                    borderBottom:
                      index < addressSuggestions.length - 1 ? `1px solid ${palette.hairline}` : "none",
                    color: palette.ink,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = palette.paper)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddressSelect(null, suggestion);
                    setAddressSuggestions([]);
                  }}
                >
                  {typeof suggestion === "string" ? suggestion : suggestion.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <FormInput
              label="City *"
              name="city"
              value={restaurantData.city}
              onChange={handleChange}
              required
              placeholder="New York"
            />
          </div>
          <FormInput
            label="State"
            name="state"
            value={restaurantData.state}
            onChange={handleChange}
            placeholder="NY"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <FormInput
            label="Zip code"
            name="zipCode"
            value={restaurantData.zipCode}
            onChange={handleChange}
            placeholder="12345"
          />
          <div className="col-span-2">
            <FormInput
              icon={Phone}
              label="Phone number *"
              name="phone"
              value={restaurantData.phone}
              onChange={(e) =>
                setRestaurantData({ ...restaurantData, phone: formatPhoneNumber(e.target.value) })
              }
              required
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        <FormInput
          icon={Globe}
          label="Website (optional)"
          name="website"
          value={restaurantData.website}
          onChange={handleChange}
          placeholder="https://yourrestaurant.com"
        />

        <div className="space-y-3">
          <Label style={labelStyle}>Halal certificate</Label>
          <p className="text-xs" style={{ color: palette.muted }}>
            Upload your halal certification (PDF or image, max 10MB).
          </p>

          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            className="hidden"
            id="certificate-upload"
          />

          {certificatePreview && (
            <div
              className="rounded-xl p-4"
              style={{ background: palette.paper, border: `1px solid ${palette.hairline}` }}
            >
              <img
                src={certificatePreview}
                alt="Certificate preview"
                className="mx-auto max-h-40 rounded object-contain"
              />
            </div>
          )}

          {certificateFile && !certificatePreview && (
            <div
              className="flex items-center gap-3 rounded-xl p-3"
              style={{ background: palette.paper, border: `1px solid ${palette.hairline}` }}
            >
              <span className="truncate text-sm" style={{ color: palette.ink }}>
                {certificateFile.name}
              </span>
            </div>
          )}

          <label htmlFor="certificate-upload" className="block">
            <div
              className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-xl transition-colors hover:opacity-90"
              style={{
                border: `2px dashed ${BRAND.gold}60`,
                background: "#ffffff",
                color: palette.muted,
              }}
            >
              <Upload className="h-4 w-4" />
              <span className="text-sm font-medium">
                {certificateFile ? "Change file" : "Upload certificate"}
              </span>
            </div>
          </label>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: palette.hairline }} />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 font-medium uppercase" style={{ background: palette.paper, color: palette.muted, letterSpacing: "0.14em" }}>
                Or
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText size={16} style={{ color: palette.muted }} />
              <Label style={labelStyle}>Sourcing explanation</Label>
            </div>
            <p className="text-xs" style={{ color: palette.muted }}>
              Don't have a certificate? Explain where your products are sourced from and how you
              ensure they are halal.
            </p>
            <Textarea
              value={sourcingExplanation}
              onChange={(e) => setSourcingExplanation(e.target.value)}
              placeholder="e.g., We source our meat from [supplier name], which is a certified halal supplier…"
              className="min-h-[100px] resize-none rounded-xl border text-[15px] transition-colors focus-visible:ring-0 focus-visible:border-[#bf9a2f] focus-visible:shadow-[0_0_0_3px_rgba(191,154,47,0.15)]"
              style={inputStyle}
              maxLength={2000}
            />
            {sourcingExplanation.length > 0 && (
              <p className="text-right text-xs" style={{ color: palette.muted }}>
                {sourcingExplanation.length}/2000
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <PillButton
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => navigate("/halal")}
            className="flex-1 justify-center"
          >
            Cancel
          </PillButton>
          <PillButton
            type="submit"
            variant="primary"
            disabled={loading}
            className="flex-[2] justify-center"
            size="lg"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Continue
                <span aria-hidden>→</span>
              </>
            )}
          </PillButton>
        </div>
      </form>
    </div>
  );

  return (
    <EditorialShell>
      <PatternTile opacity={0.04} />

      <div className="container relative mx-auto px-4 py-6 sm:py-10 lg:px-10">
        {/* Header with progress */}
        <div className="mb-8 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            className="-ml-2 inline-flex items-center gap-2 text-sm transition-colors hover:opacity-70"
            style={{ color: palette.muted, fontFamily: EDITORIAL_FONT.sans }}
          >
            <ArrowLeft className="h-4 w-4" />
            Home
          </button>

          <div className="mx-auto flex max-w-[320px] flex-1 items-center gap-2">
            {stepLabels.map((label, index) => {
              const step = index + 1;
              return (
                <div key={step} className="flex flex-1 flex-col items-center gap-1.5">
                  <div
                    className="h-[2px] w-full rounded-full transition-all duration-500"
                    style={{
                      background:
                        currentStep > step
                          ? BRAND.gold
                          : currentStep === step
                          ? BRAND.gold + "99"
                          : palette.hairline,
                    }}
                  />
                  <span
                    className="flex items-baseline gap-1 whitespace-nowrap"
                    style={{
                      fontFamily: EDITORIAL_FONT.sans,
                      fontSize: 9,
                      fontWeight: 600,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: currentStep >= step ? BRAND.gold : palette.muted,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: EDITORIAL_FONT.serif,
                        fontStyle: "italic",
                        fontWeight: 400,
                        textTransform: "none",
                        letterSpacing: "-0.01em",
                        fontSize: 12,
                      }}
                    >
                      ({String(step).padStart(2, "0")})
                    </span>
                    <span className="hidden sm:inline">{label}</span>
                  </span>
                </div>
              );
            })}
          </div>

          <div className="w-16" />
        </div>

        {/* Main content */}
        <div className="flex items-center justify-center">
          <EditorialCard
            variant="paper"
            className={`relative w-full overflow-hidden ${currentStep === 2 ? "max-w-3xl" : "max-w-lg"}`}
          >
            <div className="relative p-5 sm:p-8">
              <div key={currentStep} className="animate-[editorialFadeUp_0.5s_ease-out_both]">
                {currentStep === 1 && renderRestaurantForm()}
                {currentStep === 2 && renderPlanSelection()}
                {currentStep === 3 && renderStripeCheckout()}
              </div>
            </div>
          </EditorialCard>
        </div>
      </div>

      <style>{`
        @keyframes editorialFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </EditorialShell>
  );
}
